import React from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Stack,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  History as HistoryIcon,
} from "@mui/icons-material";
import { WorkflowRun } from "./WorkflowTypes.ts";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers (local, shared with the parent page via import)
// ─────────────────────────────────────────────────────────────────────────────

export const statusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "completed":
    case "success":
      return "#4caf50";
    case "failed":
    case "error":
      return "#f44336";
    case "running":
      return "#2196f3";
    case "queued":
      return "#ff9800";
    case "paused":
    case "waiting_for_input":
      return "#9c27b0";
    default:
      return "#9e9e9e";
  }
};

export const formatDate = (dateStr?: string) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export interface WorkflowRunHistorySidebarProps {
  isOpen: boolean;
  runs: WorkflowRun[];
  selectedRunNumber: number | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  totalRuns: number;
  onToggleOpen: () => void;
  onToggleClose: () => void;
  onSelectRun: (run: WorkflowRun) => void;
  onLoadMore: () => void;
}

const WorkflowRunHistorySidebar: React.FC<WorkflowRunHistorySidebarProps> = ({
  isOpen,
  runs,
  selectedRunNumber,
  hasMore,
  isLoadingMore,
  totalRuns,
  onToggleOpen,
  onToggleClose,
  onSelectRun,
  onLoadMore,
}) => {
  return (
    <>
      {/* Expand button when closed */}
      {!isOpen && (
        <IconButton
          onClick={onToggleOpen}
          size="small"
          sx={{
            position: "absolute",
            left: 0,
            top: 16,
            zIndex: 10,
            bgcolor: "white",
            border: "1px solid rgba(0,0,0,0.45)",
            borderRadius: "0 8px 8px 0",
            "&:hover": { bgcolor: "#f0f0f0" },
            mt: 2,
          }}
        >
          <HistoryIcon fontSize="small" />
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      )}

      {/* Sidebar panel */}
      <Box
        sx={{
          width: isOpen ? { xs: "180px", sm: "200px", md: "220px" } : "0px",
          minWidth: isOpen ? { xs: "180px", sm: "200px", md: "220px" } : "0px",
          maxWidth: isOpen ? { xs: "180px", sm: "200px", md: "220px" } : "0px",
          transition: "width 0.3s ease",
          overflow: "hidden",
          borderRight: isOpen ? "1px solid rgba(0,0,0,0.12)" : "none",
          display: "flex",
          flexDirection: "column",
          whiteSpace: "nowrap",
          mt: 1,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            pl: 2,
            pr: 1,
            pb: 1,
            pt: 0.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <HistoryIcon />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            History
          </Typography>
          <IconButton size="small" onClick={onToggleClose}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Run list */}
        <List
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            px: 0.2,
            "&::-webkit-scrollbar": { width: "9px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.1)",
              borderRadius: "10px",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.2)" },
            },
          }}
        >
          {runs.length === 0 && (
            <Typography
              variant="caption"
              sx={{ px: 2, color: "text.secondary" }}
            >
              No runs yet
            </Typography>
          )}
          {runs.map((run) => {
            const runNum = run.run_number ?? (run as any).id;
            const isSelected = selectedRunNumber === runNum;
            const st = run.status?.toLowerCase();
            return (
              <ListItem key={runNum} disablePadding sx={{ mb: 0.1 }}>
                <ListItemButton
                  selected={isSelected}
                  sx={{ borderRadius: 2 }}
                  onClick={() => onSelectRun(run)}
                >
                  <Stack direction="column" spacing={0.5} sx={{ width: "100%" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <ListItemText
                        primary={`Run #${runNum}`}
                        secondary={`v${run.workflow_version ?? run.version ?? 1}`}
                        primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                        secondaryTypographyProps={{ variant: "caption" }}
                      />
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          bgcolor: statusColor(st),
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", textTransform: "capitalize" }}
                      >
                        {st ?? "unknown"}
                      </Typography>
                    </Box>

                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {formatDate(run.started_at ?? run.created_at)}
                    </Typography>

                    <Divider />
                  </Stack>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        {/* Load more */}
        <Box sx={{ p: 2, borderTop: "1px solid rgba(0,0,0,0.08)" }}>
          {hasMore ? (
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={onLoadMore}
              disabled={isLoadingMore}
              sx={{ textTransform: "none", borderRadius: 99 }}
            >
              {isLoadingMore ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  Loading…
                </>
              ) : (
                `Load More (${runs.length} of ${totalRuns})`
              )}
            </Button>
          ) : (
            <Typography
              variant="caption"
              sx={{ textAlign: "center", display: "block", color: "text.secondary" }}
            >
              {runs.length > 0 ? `All ${totalRuns} runs loaded` : "No runs yet"}
            </Typography>
          )}
        </Box>
      </Box>
    </>
  );
};

export default WorkflowRunHistorySidebar;
